import moment from 'moment-timezone';

import { Language } from '@/const/enums';

export default class DateHelper {
  /**
   * Is the given date expired (older then now and not in the future)
   * @param date
   * @returns {boolean}
   */
  static isDateExpired(date: Date) {
    return date.getTime() < new Date().getTime();
  }

  /**
   * Is now between the given hours.
   *
   * @param time1 (i.e. '1300' which is equal to 01:00 PM, )
   * @param time2
   * @param zoneId (i.e. Los_Angeles/America)
   * @return true if it's in between times and false otherwiese
   */
  static isNowBetweenHours(start: Opt<string>, end: Opt<string>, timezone?: Opt<string>): boolean {
    if (!timezone) {
      return true;
    }

    // get the current date and time in the given time zone
    const now = moment.tz(timezone);
    const today = moment.tz(timezone).format('YYYY-MM-DD');

    // Get the exact open and close times on that date in the given time zone
    const storeOpenTime = moment.tz(today + ' ' + start, 'YYYY-MM-DD HHmm', timezone);
    const storeCloseTime = moment.tz(today + ' ' + end, 'YYYY-MM-DD HHmm', timezone);

    let check;
    if (storeCloseTime.isBefore(storeOpenTime, 'hour')) {
      // Handle ranges that span over midnight
      check = now.isAfter(storeOpenTime) || now.isBefore(storeCloseTime);
    } else {
      // Normal range check using an inclusive start time and exclusive end time
      check = now.isBetween(storeOpenTime, storeCloseTime, undefined, '[)');
    }
    // moment(Date()).format('HHmm');
    return check;
  }
  /**
   * Is the given date expired (older then now and not in the future)
   * @param date
   * @returns {boolean}
   */
  static isDateNDaysInThePast(date: Opt<Date>, nDays: number) {
    if (!date) {
      return false;
    }
    const today = new Date();
    const nowTime = today.getTime();
    const time = date.getTime();
    const seconds = nDays * 86400 * 1000;
    const response = nowTime - time > seconds;
    //86400000;
    return response;
  }
  /**
   * Is the given date expired (older then now and not in the future)
   * @param date
   * @param nMinutes The minutes to check if the given date is beyond it
   * @returns {boolean}
   */
  static isDateNMinutesInThePast(date: Date, nMinutes: number) {
    return DateHelper.isDateNSecondsInThePast(date, nMinutes * 60);
  }

  /**
   * Is the given date expired (older then now and not in the future)
   * @param date
   * @param nSeconds The minutes to check if the given date is beyond it
   * @returns {boolean}
   */
  static isDateNSecondsInThePast(date: Date, nSeconds: number) {
    const diff = new Date().getTime() - date.getTime();
    const value = diff > nSeconds * 1000;
    return value;
  }
  /**
   * Get the date plus given seconds to the future.
   *
   * @param secondsToAdd Amount of seconds to the future.
   * @returns {Date}
   */
  static addSecondsToToday(secondsToAdd: number) {
    return DateHelper.addSecondsToDate(new Date(), secondsToAdd);
  }

  /**
   * Get the date plus given seconds to the future.
   *
   * @param date The date to add to.
   * @param secondsToAdd Amount of seconds to the future.
   * @returns {Date}
   */
  static addSecondsToDate(date: Date, secondsToAdd: number) {
    return new Date(date.getTime() + secondsToAdd * 1000);
  }

  /**
   * Get a date from a given date in Z format (i.e. 2018-01-20T08:00:00.000Z) and apply the given eta.
   *
   * @param dateString (i.e. 2018-01-20T08:00:00.000Z)
   * @param etaInSeconds The estimated time in seconds
   * @return The new date
   */
  public static getDateAfterApplyingETA(dateString: string, etaInSeconds: number): Date {
    const orderDate = DateHelper.convertZFormatStringDateToDate(dateString);
    return DateHelper.addSecondsToDate(orderDate, etaInSeconds);
  }

  /**
   * Convert the given date (i.e. 23/02/2018) to an American format (i.e. 02/23/2018)
   *
   * @param date The date (i.e. 23/02/2018)
   * @return {*} (i.e. 02/23/2018)
   */
  static convertRHFormatToAmericaFormat(date: string) {
    return moment(date, 'DD/MM/YYYY').format('MM/DD/YYYY');
  }
  /**
   * Convert the given date to a string (i.e. 23/02/2018)
   *
   * @param date The date object
   * @return {*} (i.e. 23/02/2018)
   */
  static convertDateToString(date: Date) {
    return moment(date).format('DD/MM/YYYY');
  }
  /**
   * Convert the given days (i.e. 356) to months (i.e. 12)
   *
   * @param days The # of months
   * @return {number} The number of days
   */
  static convertDaysToMonths(days: number) {
    return Math.ceil(moment.duration({ days }).asMonths());
  }
  /**
   * Convert the given date (i.e. 23/02/2018) to formatted month name and day (i.e. February, 23)
   *
   * @param date The date (i.e. 23/02/2018)
   * @return {*} (i.e. February, 23)
   */
  static formatDateToMonthNameAndDay(date: Date) {
    return moment(date, 'DD/MM/YYYY').format('MMMM DD');
  }
  /**
   * Formats the given date (i.e. 2018-01-20T08:00:00.000Z) to a readable date (i.e. 04/02/2019)
   *
   * @param date The date (i.e. 2018-01-20T08:00:00.000Z)
   * @return {*} Readable date (i.e. 04/02/2019)
   */
  static formatDateToReadableDate(date: Date) {
    return moment(date).format('DD/MM/YYYY');
  }
  /**
   * Formats the given date (i.e. 2018-01-20T08:00:00.000Z) to a readable date (i.e. 04/02/2019 08:10 am)
   *
   * @param dateString The date (i.e. 2018-01-20T08:00:00.000Z)
   * @return {*} Readable date (i.e. 04/02/2019 08:10 am)
   */
  static formatDateToReadableDateAndTime(dateString: string, language: string) {
    const date = DateHelper.convertZFormatStringDateToDate(dateString);
    return moment(date, undefined, language, false).format('dddd Do MMMM hh:mm a');
  }
  /**
   * Formats the given date (i.e. 2018-01-20T08:00:00.000Z) to a readable date (i.e. 04/02/2019)
   *
   * @param dateString The date string (i.e. 2018-01-20T08:00:00.000Z)
   * @return {*} Readable date (i.e. 04/02/2019)
   */
  static formatDateStringToReadableDate(dateString: string) {
    const date = DateHelper.convertZFormatStringDateToDate(dateString);
    return DateHelper.formatDateToReadableDate(date);
  }
  /**
   * Formats the given date (i.e. 2018-01-20T08:00:00.000Z) to a readable date (i.e. 00:11)
   *
   * @param date The date (i.e. 2018-01-20T08:00:00.000Z)
   * @return {*} Readable date (i.e. 00:11)
   */
  static formatDateToReadableTime(date: Date): string {
    const str = moment(date).format('hh:mm a');
    return str;
  }
  /**
   * Formats the given date (i.e. 2018-01-20T08:00:00.000Z) to a readable date (i.e. 00:11)
   *
   * @param dateString The date string (i.e. 2018-01-20T08:00:00.000Z)
   * @return {*} Readable date (i.e. 00:11)
   */
  static formatDateStringToReadableTime(dateString: string) {
    const date = DateHelper.convertZFormatStringDateToDate(dateString);
    return DateHelper.formatDateToReadableTime(date);
  }
  /**
   * Is the given date A older than the given date B
   *
   * @param dateA
   * @param dateB
   * @return True or False
   */
  static isDateAOlderThanDateB(dateA: Date, dateB: Date) {
    return dateA.getDate() < dateB.getDate();
  }
  /**
   * Is the given date A older than the given date B
   *
   * @param dateStringA The date (i.e. 2015-08-11T13:00:00.000000Z)
   * @param dateStringB The date (i.e. 2015-08-11T13:00:00.000000Z)
   * @return True or False
   */
  static isStringDateAOlderThanStringDateB(dateStringA: string, dateStringB: string) {
    const dateA = DateHelper.convertZFormatStringDateToDate(dateStringA);
    const dateB = DateHelper.convertZFormatStringDateToDate(dateStringB);
    return dateA.getDate() < dateB.getDate();
  }
  /**
   * Convert the given date (i.e. 23/02/2018) to a javascript date
   *
   * @param date The date (i.e. 23/02/2018)
   * @return {*} (i.e. Javascript date)
   */
  static convertStringDateToDate(date: Date) {
    return moment(date, 'DD/MM/YYYY').toDate();
  }
  /**
   * Convert the given date (i.e. 2015-08-11T13:00:00.000000Z) to a javascript date
   *
   * @param date The date (i.e. 2015-08-11T13:00:00.000000Z)
   * @return {*} (i.e. Javascript date)
   */
  static convertZFormatStringDateToDate(date: string) {
    return moment(date, 'YYYY-MM-DDTHH:mm:ss.SSSSZ').toDate();
  }
  /**
   * Calculate the age from a given birthday
   *
   * @param birthday
   * @return {number} The birthday
   */
  static calculateAge(birthday: Date) {
    // birthday is a date
    return moment().diff(moment(birthday, 'DD/MM/YYYY'), 'years');
  }
  /**
   * Take a given minutes and add it to given date time and return a readable time string .
   *
   * @param date The given date
   * @param minutes The minutes to add
   * @return {*} Readable date (i.e. 00:11)
   */
  static calculateFutureTimeFromMinutes(date: Date, minutes: number) {
    const value = moment(date, 'hh:mm:ss a"').add(minutes, 'm').format('hh:mm a');
    return value;
  }
  /**
   * Get the elapsed time from a given date.
   *
   * @param date The date
   * @return the elapsed time (i.e. 2 Mins ago)
   */
  static elapsedTime(date: Date) {
    return moment(date, 'YYYY-MM-DDTHH:mm:ss.SSSSZ').startOf('minute').fromNow();
  }

  /**
   * Get the elapsed time from a given date.
   *
   * @param date The date
   * @return the elapsed time (i.e. 2 Mins ago)
   */
  static elapsedTimeInMinsValue(date: Date): number {
    let elapsed = DateHelper.convertMillisecondsToSeconds(date.getTime() - new Date().getTime());
    if (elapsed < 0) {
      elapsed = 0;
    }
    debugger;
    return elapsed;
  }

  /**
   * Get the elapsed time from a given date.
   *
   * @param date The date
   * @return the elapsed time (i.e. 2 Mins ago)
   */
  static elapsedTimeUtilNowInMinsValue(pastDate: Date): number {
    let elapsed = DateHelper.convertMillisecondsToSeconds(
      new Date().getTime() - pastDate.getTime(),
    );
    if (elapsed < 0) {
      elapsed = 0;
    }
    return elapsed;
  }

  static convertMillisecondsToSeconds(millis: number): number {
    return millis / 1000;
  }

  static convertMinutesToMilliseconds(mins: number): number {
    return mins * 60 * 1000;
  }

  static getMinsInArabic(number: Opt<string>, rtl: boolean): string {
    if (!rtl) {
      return number + ' mins';
    }
    if (!number) {
      return '';
    }
    if (number == '2') {
      return ' دقيفتين';
    }

    if (number.length <= 2 && Number(number) <= 10) {
      return number + ' دقائق';
    }

    return number + ' دقيقة';
  }

  /**
   * Convert the given hour to a string (i.e. 14:30)
   *
   * @param hour The hour string (14:00)
   * @return {*} (i.e. 23/02/2018)
   */
  public static convertHourToDate(hour: string, nextDay = false): Date {
    const randomDate = nextDay ? '2014-08-19T' : '2014-08-18T';
    return new Date(`${randomDate}${hour.substr(0, 2)}:${hour.substr(2, 2)}:00`);
  }

  public static convertHourToDateAndFormatIt(hour: Opt<string>): string {
    if (!hour) {
      return '';
    }
    return DateHelper.formatDateToReadableTime(DateHelper.convertHourToDate(hour));
  }
}
//# sourceMappingURL=DateHelper.js.map
